import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChartLineUp, Star, Lightning, Trophy, SignOut, User, Broadcast } from "@phosphor-icons/react";
import { fetchWatchlist, getUserToken, api } from "../lib/api";
import { useAuth, isSubscribed } from "../context/AuthContext";

export function Header() {
  const loc = useLocation();
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [picksCount, setPicksCount] = useState(0);
  const [resultsTotal, setResultsTotal] = useState(0);
  const [resultsAccuracy, setResultsAccuracy] = useState(0);
  const [liveNowCount, setLiveNowCount] = useState(0);
  const token = getUserToken();
  const subscribed = isSubscribed(user);

  useEffect(() => {
    let cancelled = false;
    if (!subscribed) return;
    const load = async () => {
      try {
        const watch = await fetchWatchlist(token);
        if (!cancelled) setPicksCount(watch.count || 0);
      } catch (e) {
        if (process.env.NODE_ENV !== "production") console.warn("Header: watchlist fetch failed", e);
      }
      try {
        const { data } = await api.get("/results/summary");
        if (!cancelled) {
          setResultsTotal(data?.total || 0);
          setResultsAccuracy(data?.accuracy || 0);
        }
      } catch (e) {
        if (process.env.NODE_ENV !== "production") console.warn("Header: results fetch failed", e);
      }
      try {
        const { data } = await api.get("/props/live-now");
        if (!cancelled) setLiveNowCount(data?.count || 0);
      } catch (e) {
        if (process.env.NODE_ENV !== "production") console.warn("Header: live-now fetch failed", e);
      }
    };
    load();
    const interval = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [token, loc.pathname, subscribed]);

  const handleLogout = async () => { await logout(); nav("/login"); };

  return (
    <header className="border-b border-[#27272A] sticky top-0 z-30 bg-[#0A0A0A]/95 backdrop-blur" data-testid="site-header">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <Lightning size={22} weight="fill" color="#FF4500" />
          <div className="flex items-baseline gap-2">
            <span className="font-head text-xl text-white tracking-tight">GP<span style={{ color: "#FF4500" }}> PICKS</span></span>
            <span className="overline hidden md:inline">Analytics Terminal</span>
          </div>
        </Link>
        <nav className="flex items-center gap-6">
          {subscribed && (
            <>
              <Link to="/" className={`overline transition-colors ${loc.pathname === "/" ? "text-white" : "hover:text-white"}`} data-testid="nav-board">
                <span className="inline-flex items-center gap-1.5"><ChartLineUp size={14} weight="bold" />Board</span>
              </Link>
              {liveNowCount > 0 && (
                <Link to="/live" className={`overline transition-colors ${loc.pathname === "/live" ? "text-white" : "hover:text-white"}`} data-testid="nav-live-now">
                  <span className="inline-flex items-center gap-1.5">
                    <Broadcast size={14} weight="fill" style={{ color: "#ef4444" }} />
                    <span className="inline-flex items-center gap-1">
                      <span
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ background: "#ef4444", animation: "pulse 1.5s ease-in-out infinite" }}
                      />
                      Live
                    </span>
                    <span className="font-head text-[10px] px-1.5 py-0.5 leading-none" style={{ background: "#ef4444", color: "#0A0A0A" }} data-testid="nav-live-now-count">
                      {liveNowCount}
                    </span>
                  </span>
                </Link>
              )}
              {resultsTotal > 0 && (
                <Link to="/results" className={`overline transition-colors ${loc.pathname === "/results" ? "text-white" : "hover:text-white"}`} data-testid="nav-results">
                  <span className="inline-flex items-center gap-1.5">
                    <Trophy size={14} weight="bold" />Results
                    <span className="font-head text-[10px] px-1.5 py-0.5 leading-none" style={{ background: "#22c55e", color: "#0A0A0A" }} data-testid="nav-results-acc">
                      {resultsAccuracy}%
                    </span>
                  </span>
                </Link>
              )}
              <Link to="/watchlist" className={`overline transition-colors ${loc.pathname === "/watchlist" ? "text-white" : "hover:text-white"}`} data-testid="nav-watchlist">
                <span className="inline-flex items-center gap-1.5">
                  <Star size={14} weight="bold" />My Picks
                  {picksCount > 0 && (
                    <span className="font-head text-[10px] px-1.5 py-0.5 leading-none" style={{ background: "#FF4500", color: "#0A0A0A" }} data-testid="nav-picks-count">
                      {picksCount}
                    </span>
                  )}
                </span>
              </Link>
            </>
          )}

          {user && user !== false ? (
            <div className="flex items-center gap-3 pl-6 border-l border-[#27272A]">
              <span className="overline hidden md:inline-flex items-center gap-1.5" data-testid="user-email">
                <User size={12} />
                {user.email}
              </span>
              {!subscribed && (
                <Link to="/paywall" className="pick-over px-3 py-1.5 overline" data-testid="nav-subscribe">Subscribe</Link>
              )}
              <button onClick={handleLogout} className="overline hover:text-white transition-colors" data-testid="logout-btn">
                <span className="inline-flex items-center gap-1"><SignOut size={12} />Sign out</span>
              </button>
            </div>
          ) : user === false ? (
            <Link to="/login" className="overline pl-6 border-l border-[#27272A] hover:text-white transition-colors" data-testid="nav-login">Sign in</Link>
          ) : null}
        </nav>
      </div>
    </header>
  );
}
